import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import EmergencySection from "@/components/EmergencySection";
import JourneyPhotos from "@/components/JourneyPhotos";
import ReportModal from "@/components/ReportModal";
import RideTrackingMap from "@/components/RideTrackingMap";
import RideCountdownNotification, { useRideCountdown } from "@/components/RideCountdownNotification";
import LocationPicker from "@/components/LocationPicker";
import notificationService from "@/services/NotificationService";
import { generateInvoicePDF, generateReceiptPDF } from "@/utils/pdfGenerator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin, Navigation, Car, Clock, History, Plus, X as XIcon,
  CheckCircle, Plane, Mountain, Briefcase, User, Phone, Mail,
  Wallet, CreditCard, IndianRupee, ArrowUpRight, ArrowDownLeft,
  Receipt, Star, Bell, MessageSquare, TicketCheck, Send, Edit,
  Save, MapPinned, Bookmark, CircleDot, AlertTriangle, DollarSign, Map,
  Locate, Download
} from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const statusColors = {
  requested: "bg-amber-100 text-amber-700",
  assigned: "bg-blue-100 text-blue-700",
  accepted: "bg-indigo-100 text-indigo-700",
  started: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const serviceIcons = {
  local_ride: Car, local_city_ride: Car, airport_transfer: Plane,
  outstation_trip: Mountain, tourist_package: Mountain,
  personal_booking: User, corporate_booking: Briefcase,
};

const statusSteps = ["requested", "assigned", "accepted", "started", "completed"];

export default function CustomerDashboard() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [reportModal, setReportModal] = useState({ open: false, rideId: null });
  const [trackingDialog, setTrackingDialog] = useState({ open: false, ride: null });

  const [rides, setRides] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [booking, setBooking] = useState({ 
    service_type: "", 
    pickup_location: "", 
    drop_location: "", 
    from_destination_id: "", 
    to_destination_id: "",
    pickup_coordinates: null,
    drop_coordinates: null,
    passenger_count: 1,
    passenger_names: [],
  });
  const [loading, setLoading] = useState(false);
  const [farePreview, setFarePreview] = useState(null);

  // Countdown hook for scheduled rides
  const upcomingRides = useRideCountdown(token);

  // Wallet
  const [walletBalance, setWalletBalance] = useState(0);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeMethod, setRechargeMethod] = useState("upi");
  const [recharging, setRecharging] = useState(false);
  const [upiDetails, setUpiDetails] = useState(null);
  const [upiReference, setUpiReference] = useState("");

  // Referral
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  // Dialogs
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, data: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, ride: null });
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1: select method, 2: show UPI details, 3: enter UTR
  const [paymentUtr, setPaymentUtr] = useState("");
  const [ratingDialog, setRatingDialog] = useState({ open: false, ride: null });
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingReview, setRatingReview] = useState("");
  const [messageDialog, setMessageDialog] = useState({ open: false, ride: null });
  const [messages, setMessages] = useState([]);
  const [fareProposalMode, setFareProposalMode] = useState(false);
  const [proposedFare, setProposedFare] = useState("");
  const [fareNote, setFareNote] = useState("");
  const [newMessage, setNewMessage] = useState("");

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: "", message: "", category: "general" });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", email: "", phone: "" });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "", address: "" });
  const [customerProfile, setCustomerProfile] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
  const fetchOpts = token ? { headers } : { headers, credentials: "include" };

  const fetchRides = useCallback(async () => { try { const r = await safeFetch(`${API}/rides/my`, fetchOpts); if (r.ok) setRides(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchServiceTypes = useCallback(async () => { try { const r = await safeFetch(`${API}/service-types?context=booking`); if (r.ok) setServiceTypes(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchDestinations = useCallback(async () => { try { const r = await safeFetch(`${API}/destinations`); if (r.ok) setDestinations(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchWallet = useCallback(async () => { try { const r = await safeFetch(`${API}/wallet/balance`, fetchOpts); if (r.ok) { const d = await r.json(); setWalletBalance(d.wallet_balance); setLoyaltyPoints(d.loyalty_points); } } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchWalletTxns = useCallback(async () => { try { const r = await safeFetch(`${API}/wallet/transactions`, fetchOpts); if (r.ok) setWalletTransactions(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchTickets = useCallback(async () => { try { const r = await safeFetch(`${API}/tickets`, fetchOpts); if (r.ok) setTickets(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchNotifications = useCallback(async () => { try { const r = await safeFetch(`${API}/notifications`, fetchOpts); if (r.ok) setNotifications(await r.json()); } catch (e) { console.error("Operation failed:", e); } }, []);
  const fetchProfile = useCallback(async () => {
    try { const r = await safeFetch(`${API}/customer/profile`, fetchOpts); if (r.ok) { const d = await r.json(); setCustomerProfile(d); setSavedAddresses(d.saved_addresses || []); setProfileForm({ name: d.name || "", email: d.email || "", phone: d.phone || "" }); } } catch (e) { console.error("Operation failed:", e); }
  }, []);
  const fetchReferralInfo = useCallback(async () => {
    try { const r = await safeFetch(`${API}/referral/info`, fetchOpts); if (r.ok) setReferralInfo(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);
  const fetchUpiDetails = useCallback(async () => {
    try { const r = await safeFetch(`${API}/wallet/upi-details`); if (r.ok) setUpiDetails(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  }, []);

  useEffect(() => { fetchRides(); fetchServiceTypes(); fetchDestinations(); fetchWallet(); fetchUpiDetails(); }, []);
  useEffect(() => { if (activeTab === "wallet") { fetchWalletTxns(); fetchUpiDetails(); } }, [activeTab]);
  useEffect(() => { if (activeTab === "referral") fetchReferralInfo(); }, [activeTab]);
  useEffect(() => { if (activeTab === "tickets") fetchTickets(); }, [activeTab]);
  useEffect(() => { if (activeTab === "notifications") { fetchNotifications(); fetch(`${API}/notifications/read-all`, { method: "PUT", ...fetchOpts }); } }, [activeTab]);
  useEffect(() => { if (activeTab === "profile") fetchProfile(); }, [activeTab]);

  // Fare preview
  useEffect(() => {
    if (!booking.service_type) { setFarePreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await safeFetch(`${API}/fare/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ service_type: booking.service_type, from_destination_id: booking.from_destination_id || "", to_destination_id: booking.to_destination_id || "", passenger_count: booking.passenger_count || 1 }) });
        if (r.ok) setFarePreview(await r.json());
      } catch (e) { console.error("Operation failed:", e); }
    }, 300);
    return () => clearTimeout(t);
  }, [booking.service_type, booking.from_destination_id, booking.to_destination_id, booking.passenger_count]);

  // Initialize push notifications
  useEffect(() => {
    const initPush = async () => {
      const granted = await notificationService.init();
      setPushEnabled(granted);
    };
    initPush();
  }, []);

  // Request push notification permission
  const enablePushNotifications = async () => {
    const granted = await notificationService.requestPermission();
    setPushEnabled(granted);
    if (granted) {
      toast.success("Push notifications enabled!");
    } else {
      toast.error("Notification permission denied");
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!booking.service_type || !booking.pickup_location || !booking.drop_location) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const bookingData = {
        service_type: booking.service_type,
        pickup_location: booking.pickup_location,
        drop_location: booking.drop_location,
        from_destination_id: booking.from_destination_id,
        to_destination_id: booking.to_destination_id,
        pickup_coordinates: booking.pickup_coordinates ? { lat: booking.pickup_coordinates.lat, lng: booking.pickup_coordinates.lng } : null,
        drop_coordinates: booking.drop_coordinates ? { lat: booking.drop_coordinates.lat, lng: booking.drop_coordinates.lng } : null,
        passenger_count: booking.passenger_count || 1,
        passenger_names: booking.passenger_names?.filter(n => n?.trim()) || [],
      };
      const r = await safeFetch(`${API}/rides`, { method: "POST", ...fetchOpts, body: JSON.stringify(bookingData) });
      if (r.ok) { 
        toast.success("Ride booked!"); 
        setShowBooking(false); 
        setBooking({ service_type: "", pickup_location: "", drop_location: "", from_destination_id: "", to_destination_id: "", pickup_coordinates: null, drop_coordinates: null, passenger_count: 1, passenger_names: [] }); 
        setFarePreview(null); 
        fetchRides(); 
        setActiveTab("rides"); 
      }
      else { const d = await r.json(); toast.error(d.detail || "Failed"); }
    } catch (e) { console.error(e); toast.error("Network error"); } finally { setLoading(false); }
  };

  const handleCancel = async (rideId) => { try { const r = await safeFetch(`${API}/rides/${rideId}/cancel`, { method: "PUT", ...fetchOpts }); if (r.ok) { toast.success("Ride cancelled"); fetchRides(); } else { toast.error((await r.json()).detail); } } catch (e) { console.error("Operation failed:", e); } };

  const handleRecharge = async () => {
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    if (rechargeMethod === "upi" && !upiReference.trim()) { toast.error("Enter UPI transaction/reference ID"); return; }
    setRecharging(true);
    try {
      const r = await safeFetch(`${API}/wallet/recharge`, { method: "POST", ...fetchOpts, body: JSON.stringify({ amount: amt, payment_method: rechargeMethod, upi_reference: upiReference }) });
      if (r.ok) { const d = await r.json(); toast.success(d.status === "pending_approval" ? "Submitted for admin approval" : `\u20B9${amt} added`); setRechargeAmount(""); setUpiReference(""); fetchWalletTxns(); }
      else toast.error((await r.json()).detail);
    } catch (e) { console.error(e); toast.error("Network error"); } finally { setRecharging(false); }
  };

  const handlePayment = async () => {
    if (!paymentDialog.ride) return;
    if (paymentMethod === "upi" && !paymentUtr.trim()) { toast.error("Please enter UTR / Reference number"); return; }
    setProcessingPayment(true);
    try {
      const body = { ride_id: paymentDialog.ride.ride_id, payment_method: paymentMethod, amount: paymentDialog.ride.fare_amount };
      if (paymentMethod === "upi") body.upi_reference = paymentUtr.trim();
      const r = await safeFetch(`${API}/payments/process`, { method: "POST", ...fetchOpts, body: JSON.stringify(body) });
      if (r.ok) {
        const d = await r.json();
        if (d.status === "paid") toast.success("Payment successful!");
        else if (d.status === "pending_verification") toast.success("Payment submitted! Awaiting admin verification.");
        else toast.success("Payment submitted!");
        setPaymentDialog({ open: false, ride: null }); setPaymentStep(1); setPaymentUtr(""); setPaymentMethod("cash");
        fetchRides(); fetchWallet();
      }
      else toast.error((await r.json()).detail);
    } catch (e) { console.error(e); toast.error("Network error"); } finally { setProcessingPayment(false); }
  };

  const handleViewInvoice = async (rideId) => { try { const r = await safeFetch(`${API}/invoices/${rideId}`, fetchOpts); if (r.ok) setInvoiceDialog({ open: true, data: await r.json() }); } catch (e) { console.error("Operation failed:", e); } };

  const handleRate = async () => {
    if (!ratingDialog.ride) return;
    try {
      const r = await safeFetch(`${API}/ratings`, { method: "POST", ...fetchOpts, body: JSON.stringify({ ride_id: ratingDialog.ride.ride_id, rating: ratingValue, review: ratingReview }) });
      if (r.ok) { toast.success("Rating submitted!"); setRatingDialog({ open: false, ride: null }); setRatingValue(5); setRatingReview(""); }
      else toast.error((await r.json()).detail);
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleSendMessage = async () => {
    if (!messageDialog.ride || !newMessage.trim()) return;
    try {
      const r = await safeFetch(`${API}/messages`, { method: "POST", ...fetchOpts, body: JSON.stringify({ ride_id: messageDialog.ride.ride_id, content: newMessage }) });
      if (r.ok) { setNewMessage(""); const mr = await safeFetch(`${API}/messages/${messageDialog.ride.ride_id}`, fetchOpts); if (mr.ok) setMessages(await mr.json()); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleProposeFare = async () => {
    if (!messageDialog.ride || !proposedFare) { toast.error("Enter a fare amount"); return; }
    const amount = parseFloat(proposedFare);
    if (isNaN(amount) || amount <= 0) { toast.error("Enter a valid fare amount"); return; }
    try {
      const r = await safeFetch(`${API}/rides/${messageDialog.ride.ride_id}/propose-fare`, {
        method: "POST", ...fetchOpts,
        body: JSON.stringify({ proposed_fare: amount, message: fareNote })
      });
      if (r.ok) {
        toast.success("Fare proposal sent!");
        setFareProposalMode(false);
        setProposedFare("");
        setFareNote("");
        const mr = await safeFetch(`${API}/messages/${messageDialog.ride.ride_id}`, fetchOpts);
        if (mr.ok) setMessages(await mr.json());
        fetchRides();
      } else {
        const d = await r.json();
        toast.error(d.detail || "Failed to send proposal");
      }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleAcceptFare = async (proposalId) => {
    if (!messageDialog.ride) return;
    try {
      const r = await safeFetch(`${API}/rides/${messageDialog.ride.ride_id}/accept-fare/${proposalId}`, { method: "PUT", ...fetchOpts });
      if (r.ok) {
        toast.success("Fare accepted!");
        const mr = await safeFetch(`${API}/messages/${messageDialog.ride.ride_id}`, fetchOpts);
        if (mr.ok) setMessages(await mr.json());
        fetchRides();
      } else {
        toast.error((await r.json()).detail || "Failed");
      }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleRejectFare = async (proposalId) => {
    if (!messageDialog.ride) return;
    try {
      const r = await safeFetch(`${API}/rides/${messageDialog.ride.ride_id}/reject-fare/${proposalId}`, { method: "PUT", ...fetchOpts });
      if (r.ok) {
        toast.info("Fare rejected");
        const mr = await safeFetch(`${API}/messages/${messageDialog.ride.ride_id}`, fetchOpts);
        if (mr.ok) setMessages(await mr.json());
      }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const openMessages = async (ride) => {
    setMessageDialog({ open: true, ride });
    setFareProposalMode(false);
    setProposedFare("");
    setFareNote("");
    try { const r = await safeFetch(`${API}/messages/${ride.ride_id}`, fetchOpts); if (r.ok) setMessages(await r.json()); } catch (e) { console.error("Operation failed:", e); }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) { toast.error("Fill all fields"); return; }
    try {
      const r = await safeFetch(`${API}/tickets`, { method: "POST", ...fetchOpts, body: JSON.stringify(newTicket) });
      if (r.ok) { toast.success("Ticket created"); setShowNewTicket(false); setNewTicket({ subject: "", message: "", category: "general" }); fetchTickets(); }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleTicketReply = async () => {
    if (!selectedTicket || !ticketReply.trim()) return;
    try {
      const r = await safeFetch(`${API}/tickets/${selectedTicket.ticket_id}/reply`, { method: "POST", ...fetchOpts, body: JSON.stringify({ message: ticketReply }) });
      if (r.ok) { setTicketReply(""); const tr = await safeFetch(`${API}/tickets/${selectedTicket.ticket_id}`, fetchOpts); if (tr.ok) setSelectedTicket(await tr.json()); fetchTickets(); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleSaveProfile = async () => {
    try {
      const r = await safeFetch(`${API}/customer/profile`, { method: "PUT", ...fetchOpts, body: JSON.stringify(profileForm) });
      if (r.ok) { toast.success("Profile updated"); setEditingProfile(false); fetchProfile(); }
    } catch (e) { console.error(e); toast.error("Network error"); }
  };

  const handleAddAddress = async () => {
    if (!newAddr.label || !newAddr.address) { toast.error("Fill both fields"); return; }
    try {
      const r = await safeFetch(`${API}/customer/addresses`, { method: "POST", ...fetchOpts, body: JSON.stringify(newAddr) });
      if (r.ok) { setNewAddr({ label: "", address: "" }); fetchProfile(); toast.success("Address saved"); }
    } catch (e) { console.error("Operation failed:", e); }
  };

  const handleDeleteAddress = async (addrId) => {
    try { await safeFetch(`${API}/customer/addresses/${addrId}`, { method: "DELETE", ...fetchOpts }); fetchProfile(); } catch (e) { console.error("Operation failed:", e); }
  };

  const activeRides = rides.filter(r => !["completed", "cancelled"].includes(r.ride_status));
  const pastRides = rides.filter(r => ["completed", "cancelled"].includes(r.ride_status));
  const handleTabChange = (tab) => { setActiveTab(tab); if (tab === "book") setShowBooking(true); };

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={handleTabChange}>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Welcome back, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-slate-500 mt-1">Ready for your next ride?</p>
        </div>
        <button data-testid="notif-bell-header" onClick={() => setActiveTab("notifications")} className="relative p-2 hidden lg:block">
          <Bell className="w-6 h-6 text-slate-400 hover:text-slate-600" />
          {notifications.filter(n => !n.read).length > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>}
        </button>
      </div>

      {/* ===== DASHBOARD ===== */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {activeRides.length > 0 && (
            <Card className="rounded-2xl border-0 shadow-md bg-gradient-to-br from-forest to-forest-dark text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-white/70">Active Ride</span>
                  <Badge className="bg-white/20 text-white rounded-full">{activeRides[0].ride_status}</Badge>
                </div>
                <RideStatusBar status={activeRides[0].ride_status} light />
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/20">
                  <span className="text-sm text-white/60">{activeRides[0].driver_name || "Finding driver..."}</span>
                  <span className="font-heading font-bold text-xl">{"\u20B9"}{activeRides[0].fare_amount}</span>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Rides" value={rides.length} />
            <StatCard label="Active" value={activeRides.length} color="text-emerald-600" />
            <StatCard label="Wallet" value={`\u20B9${walletBalance}`} color="text-forest" onClick={() => setActiveTab("wallet")} />
            <StatCard label="Loyalty" value={loyaltyPoints} color="text-amber-600" />
          </div>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-slate-900">Quick Book</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {serviceTypes.slice(0, 4).map(st => { const Icon = serviceIcons[st.slug] || Car; return (
                  <button key={st.slug} data-testid={`quick-book-${st.slug}`} onClick={() => { setBooking({ ...booking, service_type: st.slug }); setShowBooking(true); }} className="p-4 rounded-xl border-2 border-slate-100 hover:border-forest hover:bg-forest-50 transition-all text-left group">
                    <Icon className="w-6 h-6 text-slate-400 group-hover:text-forest mb-2" />
                    <p className="font-medium text-sm text-slate-900">{st.name}</p>
                    <p className="text-xs text-slate-500">{"\u20B9"}{st.fare_amount}</p>
                  </button>
                ); })}
              </div>
              <Button data-testid="open-booking-btn" onClick={() => setShowBooking(true)} className="w-full mt-4 bg-forest hover:bg-forest-dark text-white rounded-xl py-6 font-semibold">
                <Plus className="w-5 h-5 mr-2" /> Book a Ride
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== MY RIDES ===== */}
      {activeTab === "rides" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-semibold text-slate-900">Active Rides</h2>
            <Badge className="bg-forest-50 text-forest rounded-full">{activeRides.length} active</Badge>
          </div>
          {activeRides.length === 0 ? (
            <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><Car className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No active rides</p><Button onClick={() => setShowBooking(true)} className="mt-4 bg-forest text-white rounded-full">Book a Ride</Button></CardContent></Card>
          ) : activeRides.map(ride => (
            <RideCard key={ride.ride_id} ride={ride} onCancel={handleCancel} showCancel onPay={r => { setPaymentDialog({ open: true, ride: r }); setPaymentMethod("cash"); }} onMessage={openMessages} onReport={rideId => setReportModal({ open: true, rideId })} onTrack={r => setTrackingDialog({ open: true, ride: r })} />
          ))}
        </div>
      )}

      {/* ===== WALLET ===== */}
      {activeTab === "wallet" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">My Wallet</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="wallet-main-balance" className="rounded-2xl border-0 shadow-md bg-gradient-to-br from-forest to-forest-dark text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Wallet className="w-5 h-5" /></div><span className="text-white/70 text-sm">Available Balance</span></div>
                <p className="font-heading font-bold text-4xl">{"\u20B9"}{walletBalance}</p>
                <p className="text-white/50 text-sm mt-2">Loyalty Points: {loyaltyPoints}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Recharge Wallet</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">{[100, 500, 1000, 2000].map(a => (
                  <button key={a} data-testid={`recharge-preset-${a}`} onClick={() => setRechargeAmount(String(a))} className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition ${rechargeAmount === String(a) ? "border-forest bg-forest-50 text-forest" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{"\u20B9"}{a}</button>
                ))}</div>
                <div className="flex gap-2">
                  <Input data-testid="recharge-amount-input" type="number" placeholder="Amount" value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)} className="rounded-xl" />
                  <Select value={rechargeMethod} onValueChange={setRechargeMethod}><SelectTrigger className="w-28 rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank_transfer">Bank</SelectItem></SelectContent></Select>
                </div>
                {/* UPI Payment Details */}
                {upiDetails && upiDetails.upi_id && rechargeMethod === "upi" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                    <p className="text-center text-sm font-semibold text-amber-900">Pay using any UPI app</p>
                    {upiDetails.qr_code_url && (
                      <div className="flex justify-center">
                        <img src={upiDetails.qr_code_url} alt="QR Code" className="w-40 h-40 rounded-lg border-2 border-amber-200" />
                      </div>
                    )}
                    <div className="bg-white rounded-lg p-3 flex items-center justify-between border border-amber-100">
                      <div>
                        <p className="text-xs text-slate-500">UPI ID</p>
                        <p className="font-mono text-lg font-bold text-slate-900">{upiDetails.upi_id}</p>
                      </div>
                      <Button variant="outline" size="sm" className="rounded-full text-xs border-amber-300 text-amber-700" onClick={() => { navigator.clipboard.writeText(upiDetails.upi_id); toast.success("UPI ID copied!"); }}>Copy</Button>
                    </div>
                    {upiDetails.account_holder && <p className="text-center text-xs text-amber-700">Account: {upiDetails.account_holder}</p>}
                    {upiDetails.bank_name && <p className="text-center text-xs text-amber-700">Bank: {upiDetails.bank_name}</p>}
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-700">
                    UTR / Transaction Reference No. <span className="text-red-500">*</span>
                  </label>
                  <Input data-testid="upi-reference-input" value={upiReference} onChange={e => setUpiReference(e.target.value)} placeholder={rechargeMethod === "upi" ? "Enter UTR / UPI Reference No." : "Bank transfer reference no."} className="rounded-xl mt-1 font-mono" />
                </div>
                <p className="text-xs text-slate-400">Pay the amount via UPI/Bank, then enter the transaction reference number. Admin will verify and credit your wallet.</p>
                <Button data-testid="recharge-submit-btn" onClick={handleRecharge} disabled={recharging} className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl">{recharging ? "Submitting..." : "Submit Recharge Request"}</Button>
              </CardContent>
            </Card>
          </div>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader><CardTitle className="font-heading text-lg">Transactions</CardTitle></CardHeader>
            <CardContent>{walletTransactions.length === 0 ? <p className="text-center py-6 text-slate-400">No transactions</p> : (
              <div className="space-y-2">{walletTransactions.map(txn => (
                <div key={txn.transaction_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txn.status === "pending_approval" ? "bg-amber-100" : txn.transaction_type === "credit" ? "bg-green-100" : "bg-red-100"}`}>
                      {txn.status === "pending_approval" ? <Clock className="w-4 h-4 text-amber-600" /> : txn.transaction_type === "credit" ? <ArrowDownLeft className="w-4 h-4 text-green-600" /> : <ArrowUpRight className="w-4 h-4 text-red-600" />}
                    </div>
                    <div><p className="text-sm font-medium text-slate-900">{txn.description}</p><p className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleString()}</p>
                      {txn.status === "pending_approval" && <Badge className="rounded-full text-[10px] bg-amber-100 text-amber-700 mt-1">Pending Approval</Badge>}
                      {txn.status === "rejected" && <Badge className="rounded-full text-[10px] bg-red-100 text-red-700 mt-1">Rejected</Badge>}
                    </div>
                  </div>
                  <span className={`font-bold ${txn.transaction_type === "credit" ? "text-green-600" : "text-red-600"}`}>{txn.transaction_type === "credit" ? "+" : ""}{"\u20B9"}{Math.abs(txn.amount)}</span>
                </div>
              ))}</div>
            )}</CardContent>
          </Card>
        </div>
      )}


      {/* ===== REFER & EARN ===== */}
      {activeTab === "referral" && (
        <div className="space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Refer & Earn</h2>
          <Card className="rounded-2xl border-0 shadow-md bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                  <IndianRupee className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Invite friends & both get</p>
                  <p className="font-heading font-bold text-3xl">{"\u20B9"}100</p>
                </div>
              </div>
              <p className="text-white/70 text-sm">Share your referral code with friends. When they sign up and take their first ride, you both earn {"\u20B9"}100 in wallet credits!</p>
            </CardContent>
          </Card>
          {referralInfo && (
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-sm text-slate-500">Your Referral Code</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <div data-testid="referral-code-display" className="flex-1 bg-slate-50 rounded-xl p-4 text-center">
                      <span className="font-heading font-bold text-2xl tracking-widest text-forest">{referralInfo.referral_code}</span>
                    </div>
                    <Button
                      data-testid="copy-referral-btn"
                      onClick={() => { navigator.clipboard.writeText(referralInfo.referral_code); setReferralCopied(true); toast.success("Copied!"); setTimeout(() => setReferralCopied(false), 2000); }}
                      className={`rounded-xl px-6 ${referralCopied ? "bg-green-600" : "bg-forest"} text-white`}
                    >
                      {referralCopied ? <><CheckCircle className="w-4 h-4 mr-1" /> Copied</> : "Copy"}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center p-4 bg-forest-50 rounded-xl">
                    <p className="font-heading font-bold text-3xl text-forest">{referralInfo.referral_count}</p>
                    <p className="text-sm text-slate-500 mt-1">Friends Referred</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <p className="font-heading font-bold text-3xl text-amber-600">{"\u20B9"}{referralInfo.referral_earnings}</p>
                    <p className="text-sm text-slate-500 mt-1">Total Earned</p>
                  </div>
                </div>
                <div className="pt-2">
                  <Button
                    data-testid="share-referral-btn"
                    onClick={() => {
                      const text = `Join Cab Valley and get \u20B9100 on your wallet! Use my referral code: ${referralInfo.referral_code}`;
                      if (navigator.share) navigator.share({ title: "Cab Valley Referral", text });
                      else { navigator.clipboard.writeText(text); toast.success("Referral message copied!"); }
                    }}
                    className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl py-5 font-semibold"
                  >
                    <Send className="w-5 h-5 mr-2" /> Share with Friends
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== HISTORY ===== */}
      {activeTab === "history" && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Ride History</h2>
          {pastRides.length === 0 ? <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><History className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No past rides</p></CardContent></Card> :
            pastRides.map(ride => <RideCard key={ride.ride_id} ride={ride} onViewInvoice={handleViewInvoice} onRate={r => { setRatingDialog({ open: true, ride: r }); setRatingValue(5); setRatingReview(""); }} onPay={r => { setPaymentDialog({ open: true, ride: r }); setPaymentMethod("cash"); }} onReport={rideId => setReportModal({ open: true, rideId })} />)}
        </div>
      )}

      {/* ===== TICKETS (SUPPORT) ===== */}
      {activeTab === "tickets" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-slate-900">Support Tickets</h2>
            <Button data-testid="new-ticket-btn" onClick={() => setShowNewTicket(true)} className="bg-forest text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> New Ticket</Button>
          </div>
          {selectedTicket ? (
            <Card className="rounded-2xl border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle className="font-heading text-lg">{selectedTicket.subject}</CardTitle><p className="text-xs text-slate-400 mt-1">#{selectedTicket.ticket_id.slice(-8)} - {selectedTicket.status}</p></div>
                <Button variant="outline" size="sm" onClick={() => setSelectedTicket(null)} className="rounded-full text-xs">Back</Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {selectedTicket.messages?.map((m, i) => (
                    <div key={`msg-${i}`} className={`p-3 rounded-xl ${m.role === "admin" || m.role === "super_admin" ? "bg-blue-50 ml-8" : "bg-slate-50 mr-8"}`}>
                      <div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium text-slate-700">{m.sender_name}</span><Badge className="text-[10px] rounded-full">{m.role}</Badge></div>
                      <p className="text-sm text-slate-700">{m.content}</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(m.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                {selectedTicket.status !== "closed" && (
                  <div className="flex gap-2">
                    <Input value={ticketReply} onChange={e => setTicketReply(e.target.value)} placeholder="Type a reply..." className="rounded-xl" onKeyDown={e => e.key === "Enter" && handleTicketReply()} />
                    <Button onClick={handleTicketReply} className="bg-forest text-white rounded-xl"><Send className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tickets.length === 0 ? <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><TicketCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No tickets yet</p></CardContent></Card> :
                tickets.map(t => (
                  <Card key={t.ticket_id} data-testid={`ticket-${t.ticket_id}`} className="rounded-2xl border-0 shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => setSelectedTicket(t)}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{t.subject}</p>
                        <p className="text-xs text-slate-400">{t.category} - {new Date(t.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={`rounded-full text-xs ${t.status === "open" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{t.status}</Badge>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ===== NOTIFICATIONS ===== */}
      {activeTab === "notifications" && (
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Notifications</h2>
          {notifications.length === 0 ? <Card className="rounded-2xl border-0 shadow-sm"><CardContent className="py-12 text-center"><Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No notifications</p></CardContent></Card> :
            notifications.map(n => (
              <Card key={n.notification_id} data-testid={`notif-${n.notification_id}`} className={`rounded-2xl border-0 shadow-sm ${!n.read ? "border-l-4 border-l-forest" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between"><div><p className="font-medium text-slate-900">{n.title}</p><p className="text-sm text-slate-600 mt-1">{n.message}</p></div><p className="text-xs text-slate-400 whitespace-nowrap ml-4">{new Date(n.created_at).toLocaleDateString()}</p></div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* ===== PROFILE ===== */}
      {activeTab === "profile" && (
        <div className="max-w-lg space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">My Profile</h2>
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-forest-50 flex items-center justify-center"><span className="font-heading font-bold text-2xl text-forest">{user?.name?.[0]?.toUpperCase()}</span></div>
                <div className="flex-1"><p className="font-heading font-semibold text-lg text-slate-900">{user?.name}</p><p className="text-sm text-slate-500">Customer</p></div>
                <Button variant="outline" size="sm" onClick={() => setEditingProfile(!editingProfile)} className="rounded-full text-xs"><Edit className="w-3 h-3 mr-1" /> Edit</Button>
              </div>
              {editingProfile ? (
                <div className="space-y-3">
                  <div><Label className="text-sm">Name</Label><Input value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} className="mt-1 rounded-xl" /></div>
                  <div><Label className="text-sm">Email</Label><Input value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} className="mt-1 rounded-xl" /></div>
                  <div><Label className="text-sm">Phone</Label><Input value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} className="mt-1 rounded-xl" /></div>
                  <Button onClick={handleSaveProfile} className="w-full bg-forest text-white rounded-xl"><Save className="w-4 h-4 mr-2" /> Save</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-600"><Phone className="w-5 h-5 text-slate-400" /><span>{user?.phone || "-"}</span></div>
                  <div className="flex items-center gap-3 text-slate-600"><Mail className="w-5 h-5 text-slate-400" /><span>{user?.email || "-"}</span></div>
                  <div className="flex items-center gap-3 text-slate-600"><Wallet className="w-5 h-5 text-slate-400" /><span>Wallet: {"\u20B9"}{walletBalance} | Loyalty: {loyaltyPoints} pts</span></div>
                  {customerProfile && <div className="flex items-center gap-3 text-slate-600"><Car className="w-5 h-5 text-slate-400" /><span>{customerProfile.total_rides} rides | {"\u20B9"}{customerProfile.total_spent} spent</span></div>}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Saved Addresses */}
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg flex items-center gap-2"><MapPinned className="w-5 h-5 text-forest" /> Saved Addresses</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {savedAddresses.map(a => (
                <div key={a.address_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div><p className="text-sm font-medium text-slate-900">{a.label}</p><p className="text-xs text-slate-500">{a.address}</p></div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteAddress(a.address_id)} className="text-red-400 hover:text-red-600"><XIcon className="w-4 h-4" /></Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input placeholder="Label (Home, Office...)" value={newAddr.label} onChange={e => setNewAddr({ ...newAddr, label: e.target.value })} className="rounded-xl text-sm" />
                <Input placeholder="Address" value={newAddr.address} onChange={e => setNewAddr({ ...newAddr, address: e.target.value })} className="rounded-xl text-sm flex-1" />
                <Button onClick={handleAddAddress} className="bg-forest text-white rounded-xl" size="sm"><Plus className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== EMERGENCY ===== */}
      {activeTab === "emergency" && (
        <div className="max-w-lg">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Emergency & Safety</h2>
          <EmergencySection token={token} rideId={rides.find(r => ["assigned", "accepted", "started"].includes(r.ride_status))?.ride_id} />
        </div>
      )}

      {/* ===== JOURNEY PHOTOS ===== */}
      {activeTab === "photos" && (
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">My Journey Photos</h2>
          <JourneyPhotos token={token} />
        </div>
      )}

      {/* ===== BOOKING MODAL ===== */}
      {showBooking && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg rounded-2xl shadow-xl border-0 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-xl text-slate-900">Book a Ride</CardTitle>
              <button data-testid="close-booking-btn" onClick={() => setShowBooking(false)} className="text-slate-400 hover:text-slate-600"><XIcon className="w-5 h-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBook} className="space-y-4">
                <div>
                  <Label className="text-slate-700 text-sm">Service Type</Label>
                  <Select value={booking.service_type} onValueChange={v => setBooking({ ...booking, service_type: v })}>
                    <SelectTrigger data-testid="service-type-select" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select service type" /></SelectTrigger>
                    <SelectContent>{serviceTypes.map(st => <SelectItem key={st.slug} value={st.slug}>{st.name} - {"\u20B9"}{st.fare_amount}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-700 text-sm">From Destination</Label>
                    <Select value={booking.from_destination_id} onValueChange={v => setBooking({ ...booking, from_destination_id: v })}>
                      <SelectTrigger data-testid="from-dest-select" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select origin" /></SelectTrigger>
                      <SelectContent>{destinations.map(d => <SelectItem key={d.destination_id} value={d.destination_id}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">To Destination</Label>
                    <Select value={booking.to_destination_id} onValueChange={v => setBooking({ ...booking, to_destination_id: v })}>
                      <SelectTrigger data-testid="to-dest-select" className="mt-1.5 rounded-xl"><SelectValue placeholder="Select destination" /></SelectTrigger>
                      <SelectContent>{destinations.map(d => <SelectItem key={d.destination_id} value={d.destination_id}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {farePreview && (
                  <div data-testid="fare-preview" className="bg-forest-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-forest/70">Estimated Fare</span>
                        <p className="text-xs text-forest/50">{farePreview.source === "fare_matrix" ? "Route-based pricing" : farePreview.source === "multiplier" ? "Service multiplier" : "Base fare"}</p>
                      </div>
                      <span className="font-heading font-bold text-xl text-forest" data-testid="fare-total">{"\u20B9"}{farePreview.fare_amount}</span>
                    </div>
                    {farePreview.passenger_count > 1 && (
                      <div className="mt-2 pt-2 border-t border-forest/10 flex items-center justify-between text-xs text-forest/60">
                        <span>{"\u20B9"}{farePreview.base_fare_per_person} x {farePreview.passenger_count} passengers</span>
                        <span className="font-medium text-forest/80">Total: {"\u20B9"}{farePreview.fare_amount}</span>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-slate-700 text-sm">Pickup Location</Label>
                  <LocationPicker
                    label="Select Pickup Location"
                    value={booking.pickup_coordinates}
                    onChange={(loc) => setBooking({ 
                      ...booking, 
                      pickup_coordinates: loc,
                      pickup_location: loc?.address || ""
                    })}
                    placeholder="Search or click on map to select pickup"
                    markerColor="#22c55e"
                    className="mt-1.5"
                  />
                  {/* Manual input fallback */}
                  <div className="relative mt-2">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      data-testid="pickup-input" 
                      value={booking.pickup_location} 
                      onChange={e => setBooking({ ...booking, pickup_location: e.target.value })} 
                      placeholder="Or type address manually" 
                      className="pl-10 rounded-xl text-sm" 
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Drop Location</Label>
                  <LocationPicker
                    label="Select Drop Location"
                    value={booking.drop_coordinates}
                    onChange={(loc) => setBooking({ 
                      ...booking, 
                      drop_coordinates: loc,
                      drop_location: loc?.address || ""
                    })}
                    placeholder="Search or click on map to select drop"
                    markerColor="#ef4444"
                    className="mt-1.5"
                  />
                  {/* Manual input fallback */}
                  <div className="relative mt-2">
                    <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      data-testid="drop-input" 
                      value={booking.drop_location} 
                      onChange={e => setBooking({ ...booking, drop_location: e.target.value })} 
                      placeholder="Or type address manually" 
                      className="pl-10 rounded-xl text-sm" 
                    />
                  </div>
                </div>
                {/* Passenger Count */}
                <div>
                  <Label className="text-slate-700 text-sm">Number of Passengers</Label>
                  <Select value={String(booking.passenger_count || 1)} onValueChange={v => {
                    const count = parseInt(v);
                    const names = [...(booking.passenger_names || [])];
                    while (names.length < count - 1) names.push("");
                    setBooking({ ...booking, passenger_count: count, passenger_names: names.slice(0, count - 1) });
                  }}>
                    <SelectTrigger data-testid="passenger-count-select" className="mt-1.5 rounded-xl">
                      <SelectValue placeholder="1" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "Passenger" : "Passengers"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {booking.passenger_count > 1 && (
                  <div className="space-y-2 pl-2 border-l-2 border-amber-200">
                    <p className="text-xs text-slate-500 font-medium">Additional Passenger Names</p>
                    {Array.from({ length: booking.passenger_count - 1 }).map((_, i) => (
                      <Input
                        key={i}
                        data-testid={`passenger-name-${i + 1}`}
                        placeholder={`Passenger ${i + 2} Name`}
                        value={booking.passenger_names?.[i] || ""}
                        onChange={e => {
                          const names = [...(booking.passenger_names || [])];
                          names[i] = e.target.value;
                          setBooking({ ...booking, passenger_names: names });
                        }}
                        className="rounded-xl text-sm"
                      />
                    ))}
                  </div>
                )}
                <Button data-testid="confirm-booking-btn" type="submit" disabled={loading || !booking.pickup_location || !booking.drop_location} className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl py-5 font-semibold">{loading ? "Booking..." : "Confirm Booking"}</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={o => { if (!o) { setPaymentStep(1); setPaymentUtr(""); setPaymentMethod("cash"); } setPaymentDialog({ ...paymentDialog, open: o }); }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Pay for Ride</DialogTitle></DialogHeader>
          {paymentDialog.ride && (<div className="space-y-4">
            {/* Amount Display */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Amount Due</span>
                <span className="font-heading font-bold text-xl">{"\u20B9"}{paymentDialog.ride.fare_amount}</span>
              </div>
              {paymentMethod === "upi" && paymentStep > 1 && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">UPI Payment</span>
                  {paymentStep === 2 && <span className="text-xs text-slate-400">Step 2: Make Payment</span>}
                  {paymentStep === 3 && <span className="text-xs text-slate-400">Step 3: Enter Reference</span>}
                </div>
              )}
            </div>

            {/* STEP 1: Select Payment Method */}
            {paymentStep === 1 && (
              <>
                <div className="space-y-2">
                  {[{ value: "wallet", label: "Wallet", desc: `Balance: \u20B9${walletBalance}`, icon: Wallet, disabled: walletBalance < (paymentDialog.ride?.fare_amount || 0) },
                    { value: "upi", label: "UPI", desc: "Pay via UPI & confirm with reference no.", icon: CreditCard, disabled: false },
                    { value: "cash", label: "Cash", desc: "Pay cash to driver", icon: IndianRupee, disabled: false }
                  ].map(opt => (
                    <button key={opt.value} data-testid={`payment-method-${opt.value}`} onClick={() => !opt.disabled && setPaymentMethod(opt.value)} disabled={opt.disabled}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${paymentMethod === opt.value ? "border-forest bg-forest-50" : opt.disabled ? "border-slate-100 opacity-50 cursor-not-allowed" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${paymentMethod === opt.value ? "bg-forest text-white" : "bg-slate-100 text-slate-500"}`}><opt.icon className="w-4 h-4" /></div>
                      <div><p className="text-sm font-medium text-slate-900">{opt.label}</p><p className="text-xs text-slate-500">{opt.desc}</p></div>
                      {paymentMethod === opt.value && <CheckCircle className="w-5 h-5 text-forest ml-auto" />}
                    </button>
                  ))}
                </div>
                {paymentMethod === "upi" ? (
                  <Button data-testid="payment-next-btn" onClick={() => setPaymentStep(2)} className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl py-5 font-semibold">
                    Next: View UPI Details
                  </Button>
                ) : (
                  <Button data-testid="confirm-payment-btn" onClick={handlePayment} disabled={processingPayment} className="w-full bg-forest hover:bg-forest-dark text-white rounded-xl py-5 font-semibold">
                    {processingPayment ? "Processing..." : paymentMethod === "cash" ? "Confirm Cash Payment" : `Pay \u20B9${paymentDialog.ride.fare_amount}`}
                  </Button>
                )}
              </>
            )}

            {/* STEP 2: Show UPI Details (QR + UPI ID) */}
            {paymentStep === 2 && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <p className="text-center text-sm font-semibold text-amber-900">Pay using any UPI app</p>
                  
                  {/* UPI QR Code */}
                  {(upiDetails?.qr_code_url || upiDetails?.upi_qr_url) && (
                    <div className="flex justify-center">
                      <img src={upiDetails.qr_code_url || upiDetails.upi_qr_url} alt="UPI QR Code" className="w-48 h-48 rounded-lg border-2 border-amber-200" />
                    </div>
                  )}
                  
                  {/* UPI ID with Copy */}
                  {upiDetails?.upi_id && (
                    <div className="bg-white rounded-lg p-3 flex items-center justify-between border border-amber-100">
                      <div>
                        <p className="text-xs text-slate-500">UPI ID</p>
                        <p className="font-mono text-lg font-bold text-slate-900" data-testid="upi-id-display">{upiDetails.upi_id}</p>
                      </div>
                      <Button variant="outline" size="sm" className="rounded-full text-xs border-amber-300 text-amber-700 hover:bg-amber-50" 
                        onClick={() => { navigator.clipboard.writeText(upiDetails.upi_id); toast.success("UPI ID copied!"); }}>
                        Copy
                      </Button>
                    </div>
                  )}
                  
                  {upiDetails?.account_holder && (
                    <p className="text-center text-xs text-amber-700">Account: {upiDetails.account_holder}</p>
                  )}
                  {upiDetails?.bank_name && (
                    <p className="text-center text-xs text-amber-700">Bank: {upiDetails.bank_name}</p>
                  )}
                  
                  {!upiDetails?.upi_id && (
                    <p className="text-center text-sm text-red-600 font-medium">UPI details not configured. Please contact support.</p>
                  )}
                  
                  <div className="bg-amber-100/60 rounded-lg p-2 text-center">
                    <p className="text-xs text-amber-800 font-medium">Amount to pay: <span className="text-base font-bold">{"\u20B9"}{paymentDialog.ride.fare_amount}</span></p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Instructions:</strong> Open your UPI app (GPay, PhonePe, Paytm etc.), pay the exact amount to the UPI ID shown above, then click "I've Paid" to enter your transaction reference number.
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setPaymentStep(1)}>Back</Button>
                  <Button data-testid="ive-paid-btn" className="flex-1 bg-forest hover:bg-forest-dark text-white rounded-xl font-semibold" onClick={() => setPaymentStep(3)}>
                    I've Paid - Enter Reference
                  </Button>
                </div>
              </>
            )}

            {/* STEP 3: Enter UTR / Reference Number */}
            {paymentStep === 3 && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
                  <p className="text-center text-sm font-semibold text-emerald-900">Enter Payment Confirmation</p>
                  <p className="text-xs text-center text-emerald-700">Enter the UTR number or transaction reference from your UPI app to confirm your payment.</p>
                  
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-700">UTR / Transaction Reference No. <span className="text-red-500">*</span></label>
                    <input
                      data-testid="payment-utr-input"
                      type="text"
                      value={paymentUtr}
                      onChange={(e) => setPaymentUtr(e.target.value)}
                      placeholder="e.g. 412345678901 or UPI ref number"
                      className="w-full px-4 py-3 rounded-xl border-2 border-emerald-200 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-300 text-base font-mono tracking-wide outline-none"
                      autoFocus
                    />
                    <p className="text-xs text-slate-400">You can find this in your UPI app's transaction history</p>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 text-center">
                  <p className="text-xs text-amber-700">Your payment will be verified by our team. Balance will reflect within 30 minutes.</p>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setPaymentStep(2)}>Back</Button>
                  <Button data-testid="confirm-payment-btn" onClick={handlePayment} disabled={processingPayment || !paymentUtr.trim()} 
                    className="flex-1 bg-forest hover:bg-forest-dark text-white rounded-xl font-semibold disabled:opacity-50">
                    {processingPayment ? "Submitting..." : "Confirm Payment"}
                  </Button>
                </div>
              </>
            )}
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialog.open} onOpenChange={o => setInvoiceDialog({ ...invoiceDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Invoice</DialogTitle></DialogHeader>
          {invoiceDialog.data && (<div data-testid="invoice-detail" className="space-y-4">
            <div className="text-center pb-4 border-b"><p className="font-heading font-bold text-lg">{invoiceDialog.data.platform_name}</p><p className="text-xs text-slate-500">{invoiceDialog.data.business_address}</p><p className="text-xs text-slate-400 mt-1">{invoiceDialog.data.invoice_id}</p></div>
            <div className="space-y-2 text-sm">
              {[["Booking ID", invoiceDialog.data.ride_id], ["Customer", invoiceDialog.data.customer_name], ["Service", invoiceDialog.data.service_type?.replace(/_/g, " ")], ["Pickup", invoiceDialog.data.pickup_location], ["Drop", invoiceDialog.data.drop_location], ["Driver", invoiceDialog.data.driver_name || "-"], ["Payment", (invoiceDialog.data.payment_method || "").toUpperCase()], ...(invoiceDialog.data.upi_reference ? [["UTR", invoiceDialog.data.upi_reference]] : []), ["Date", invoiceDialog.data.created_at ? new Date(invoiceDialog.data.created_at).toLocaleDateString() : "-"], ...(invoiceDialog.data.passenger_count > 1 ? [["Passengers", `${invoiceDialog.data.passenger_count} passengers`]] : [])].map(([l, v]) => <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span className="text-slate-900">{v}</span></div>)}
              {invoiceDialog.data.passenger_names?.length > 0 && (
                <div className="pt-1">
                  <span className="text-slate-500 text-xs">Passenger Names:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {invoiceDialog.data.passenger_names.map((n, i) => n && <span key={`pax-${i}`} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{n}</span>)}
                  </div>
                </div>
              )}
            </div>
            <div className="pt-4 border-t flex justify-between items-center"><span className="text-slate-500 font-medium">Total Fare</span><span className="font-heading font-bold text-2xl">{"\u20B9"}{invoiceDialog.data.fare_amount}</span></div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="download-invoice-pdf" className="flex-1 bg-forest text-white rounded-full text-xs" onClick={async () => { try { await generateInvoicePDF(invoiceDialog.data); toast.success("Invoice PDF downloaded"); } catch (e) { console.error(e); toast.error("Failed to generate PDF"); } }}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download Invoice
              </Button>
              <Button data-testid="download-receipt-pdf" variant="outline" className="flex-1 rounded-full text-xs" onClick={async () => {
                try {
                  const res = await safeFetch(`${API}/receipts/${invoiceDialog.data.ride_id}`, fetchOpts);
                  if (res.ok) { const rData = await res.json(); await generateReceiptPDF(rData); toast.success("Receipt PDF downloaded"); }
                  else toast.error("Receipt not available");
                } catch (e) { console.error(e); toast.error("Failed to generate receipt"); }
              }}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download Receipt
              </Button>
            </div>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialog.open} onOpenChange={o => setRatingDialog({ ...ratingDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Rate Your Ride</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center gap-2">{[1, 2, 3, 4, 5].map(s => <button key={s} data-testid={`rating-star-${s}`} onClick={() => setRatingValue(s)} className="p-1"><Star className={`w-8 h-8 ${s <= ratingValue ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}</div>
            <Textarea value={ratingReview} onChange={e => setRatingReview(e.target.value)} placeholder="Write a review (optional)" className="rounded-xl" rows={3} />
            <Button data-testid="submit-rating-btn" onClick={handleRate} className="w-full bg-forest text-white rounded-xl">Submit Rating</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Dialog with Fare Negotiation */}
      <Dialog open={messageDialog.open} onOpenChange={o => { setMessageDialog({ ...messageDialog, open: o }); setFareProposalMode(false); }}>
        <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading flex items-center justify-between">
              <span>Chat - Ride #{messageDialog.ride?.ride_id?.slice(-8)}</span>
              {messageDialog.ride && ["requested", "assigned", "accepted"].includes(messageDialog.ride.ride_status) && (
                <Button
                  data-testid="toggle-fare-proposal"
                  variant="outline"
                  size="sm"
                  onClick={() => setFareProposalMode(!fareProposalMode)}
                  className={`rounded-full text-xs ${fareProposalMode ? "bg-amber-100 border-amber" : ""}`}
                >
                  <DollarSign className="w-3 h-3 mr-1" /> {fareProposalMode ? "Cancel" : "Negotiate Fare"}
                </Button>
              )}
            </DialogTitle>
            {messageDialog.ride && (
              <div className="flex items-center gap-4 text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded-lg">
                <span>Current Fare: <strong className="text-forest">₹{messageDialog.ride.fare_amount}</strong></span>
                <Badge className={`rounded-full text-xs ${statusColors[messageDialog.ride.ride_status]}`}>{messageDialog.ride.ride_status}</Badge>
              </div>
            )}
          </DialogHeader>

          {/* Fare Proposal Form */}
          {fareProposalMode && (
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 space-y-3">
              <p className="text-sm font-medium text-amber-800">Propose a New Fare</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="proposed-fare-input"
                    type="number"
                    value={proposedFare}
                    onChange={e => setProposedFare(e.target.value)}
                    placeholder="Enter amount"
                    className="pl-9 rounded-xl"
                  />
                </div>
              </div>
              <Input
                data-testid="fare-note-input"
                value={fareNote}
                onChange={e => setFareNote(e.target.value)}
                placeholder="Add a note (optional)"
                className="rounded-xl text-sm"
              />
              <Button
                data-testid="send-fare-proposal"
                onClick={handleProposeFare}
                className="w-full bg-amber hover:bg-amber/90 text-white rounded-xl"
              >
                Send Proposal
              </Button>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 py-2 min-h-[150px] max-h-[300px]">
            {messages.length === 0 ? <p className="text-center text-slate-400 py-8">No messages yet. Start the conversation!</p> :
              messages.map(m => (
                <div key={m.message_id} className={`p-3 rounded-xl ${m.sender_id === user?.user_id ? "bg-forest-50 ml-8" : "bg-slate-50 mr-8"} ${m.is_fare_proposal ? "border-2 border-amber-300" : ""}`}>
                  <p className="text-xs font-medium text-slate-600 mb-1">{m.sender_name} ({m.sender_role})</p>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.content}</p>
                  {m.is_fare_proposal && m.sender_id !== user?.user_id && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        data-testid={`accept-fare-${m.proposal_id}`}
                        size="sm"
                        onClick={() => handleAcceptFare(m.proposal_id)}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs flex-1"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" /> Accept
                      </Button>
                      <Button
                        data-testid={`reject-fare-${m.proposal_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectFare(m.proposal_id)}
                        className="rounded-full text-xs text-red-500 border-red-200 flex-1"
                      >
                        <XIcon className="w-3 h-3 mr-1" /> Decline
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                </div>
              ))}
          </div>

          {/* Message Input */}
          <div className="flex gap-2 pt-2 border-t">
            <Input 
              data-testid="chat-message-input"
              value={newMessage} 
              onChange={e => setNewMessage(e.target.value)} 
              placeholder="Type a message..." 
              className="rounded-xl" 
              onKeyDown={e => e.key === "Enter" && handleSendMessage()} 
            />
            <Button onClick={handleSendMessage} className="bg-forest text-white rounded-xl"><Send className="w-4 h-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Create Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Category</Label>
              <Select value={newTicket.category} onValueChange={v => setNewTicket({ ...newTicket, category: v })}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="ride_issue">Ride Issue</SelectItem><SelectItem value="payment">Payment</SelectItem><SelectItem value="refund">Refund</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Subject</Label><Input data-testid="ticket-subject" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} className="mt-1 rounded-xl" placeholder="Brief description" /></div>
            <div><Label className="text-sm">Message</Label><Textarea data-testid="ticket-message" value={newTicket.message} onChange={e => setNewTicket({ ...newTicket, message: e.target.value })} className="mt-1 rounded-xl" rows={4} placeholder="Describe your issue..." /></div>
            <Button data-testid="submit-ticket-btn" onClick={handleCreateTicket} className="w-full bg-forest text-white rounded-xl">Submit Ticket</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={trackingDialog.open} onOpenChange={o => setTrackingDialog({ ...trackingDialog, open: o })}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Locate className="w-5 h-5 text-blue-600" /> Live Ride Tracking
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {trackingDialog.ride && (
              <RideTrackingMap
                rideId={trackingDialog.ride.ride_id}
                token={token}
                pickupLocation={trackingDialog.ride.pickup_coordinates || { lat: 34.0837, lng: 74.7973, address: trackingDialog.ride.pickup_location }}
                dropLocation={trackingDialog.ride.drop_coordinates || { lat: 34.1526, lng: 74.7891, address: trackingDialog.ride.drop_location }}
                rideStatus={trackingDialog.ride.ride_status}
                driverName={trackingDialog.ride.driver_name}
                driverPhone={trackingDialog.ride.driver_phone}
                estimatedTime={trackingDialog.ride.estimated_arrival || "Calculating..."}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <ReportModal 
        isOpen={reportModal.open} 
        onClose={() => setReportModal({ open: false, rideId: null })} 
        rideId={reportModal.rideId} 
        token={token} 
      />

      {/* Ride Countdown Notification */}
      <RideCountdownNotification 
        token={token} 
        rides={upcomingRides} 
        onRideClick={(ride) => {
          setActiveTab("rides");
          setTrackingDialog({ open: true, ride });
        }} 
      />
    </DashboardLayout>
  );
}

function StatCard({ label, value, color = "text-slate-900", onClick }) {
  return (
    <Card className={`rounded-2xl border-0 shadow-sm ${onClick ? "cursor-pointer hover:shadow-md" : ""}`} onClick={onClick}>
      <CardContent className="p-5"><p className="text-sm text-slate-500">{label}</p><p className={`font-heading font-bold text-3xl mt-1 ${color}`}>{value}</p></CardContent>
    </Card>
  );
}

function RideStatusBar({ status, light }) {
  const idx = statusSteps.indexOf(status);
  return (
    <div className="flex items-center gap-1 my-3">
      {statusSteps.map((s, i) => (
        <div key={s} className="flex-1 flex items-center gap-1">
          <div className={`w-full h-1.5 rounded-full ${i <= idx ? (light ? "bg-white/80" : "bg-forest") : (light ? "bg-white/20" : "bg-slate-200")}`} />
        </div>
      ))}
    </div>
  );
}

function RideCard({ ride, onCancel, showCancel, onViewInvoice, onPay, onRate, onMessage, onReport, onTrack }) {
  const isPending = ride.payment_status !== "paid" && ride.ride_status !== "cancelled";
  const canTrack = ["assigned", "accepted", "started"].includes(ride.ride_status) && ride.driver_id;
  return (
    <Card data-testid={`ride-card-${ride.ride_id}`} className="rounded-2xl border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge className={`rounded-full text-xs px-3 py-1 ${statusColors[ride.ride_status]}`}>{ride.ride_status}</Badge>
            <span className="text-xs text-slate-500">{ride.service_type.replace(/_/g, " ")}</span>
          </div>
          <span className="font-heading font-bold text-lg text-slate-900">{"\u20B9"}{ride.fare_amount}</span>
        </div>
        <RideStatusBar status={ride.ride_status} />
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-slate-700">{ride.pickup_location}</span></div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-slate-700">{ride.drop_location}</span></div>
        </div>
        {ride.driver_name && <p className="text-xs text-slate-500 mt-2">Driver: {ride.driver_name}</p>}
        {ride.created_at && <p className="text-xs text-slate-400 mt-1">{new Date(ride.created_at).toLocaleString()}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {canTrack && onTrack && (
            <Button data-testid={`track-ride-${ride.ride_id}`} size="sm" onClick={() => onTrack(ride)} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs">
              <Locate className="w-3 h-3 mr-1" /> Track
            </Button>
          )}
          {showCancel && !["started", "completed"].includes(ride.ride_status) && <Button data-testid={`cancel-ride-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onCancel(ride.ride_id)} className="rounded-full text-red-500 border-red-200 hover:bg-red-50 text-xs">Cancel</Button>}
          {isPending && onPay && <Button data-testid={`pay-ride-${ride.ride_id}`} size="sm" onClick={() => onPay(ride)} className="rounded-full bg-forest text-white text-xs"><CreditCard className="w-3 h-3 mr-1" /> Pay</Button>}
          {ride.ride_status === "completed" && onViewInvoice && <Button data-testid={`invoice-ride-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onViewInvoice(ride.ride_id)} className="rounded-full text-xs"><Receipt className="w-3 h-3 mr-1" /> Invoice</Button>}
          {ride.ride_status === "completed" && onRate && <Button data-testid={`rate-ride-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onRate(ride)} className="rounded-full text-xs text-amber-600 border-amber-200"><Star className="w-3 h-3 mr-1" /> Rate</Button>}
          {onMessage && ride.driver_id && !["cancelled"].includes(ride.ride_status) && <Button variant="outline" size="sm" onClick={() => onMessage(ride)} className="rounded-full text-xs"><MessageSquare className="w-3 h-3 mr-1" /> Chat</Button>}
          {onReport && ride.driver_id && !["cancelled"].includes(ride.ride_status) && (
            <Button data-testid={`report-ride-${ride.ride_id}`} variant="outline" size="sm" onClick={() => onReport(ride.ride_id)} className="rounded-full text-xs text-red-500 border-red-200 hover:bg-red-50">
              <AlertTriangle className="w-3 h-3 mr-1" /> Report
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
